import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { motion, useReducedMotion } from "framer-motion";
import { RoundedChecklistIcon } from "../../../../components/icons";
import { Stagger, StaggerItem, pressable } from "../../../../components/motion";

// The check mark springs in slightly after the page appears — the "done" moment.
const checkIconPop = {
    hidden: { opacity: 0, scale: 0.5 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: { type: "spring", stiffness: 380, damping: 18, delay: 0.15 },
    },
};

// Translation strings carry <b>/<br/> markup, so they render as HTML.
const HtmlText = ({ translationKey, values, className }) => {
    const { t } = useTranslation();
    return (
        <p
            className={className}
            dangerouslySetInnerHTML={{ __html: t(translationKey, values) }}
        />
    );
};

const BODY_TEXT = "tw:!text-[14px] tw:!font-[400] tw:!text-[#282828] tw:!m-0";
const TITLE_TEXT =
    "tw:!font-libre-caslon-display tw:!text-[24px] tw:sm:!text-[28px] tw:!font-[400] tw:!text-[#282828] tw:!text-center tw:!m-0";

const SuccessPage = ({ profileData, resendEmailHandler }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const shouldReduceMotion = useReducedMotion();

    return (
        <Stagger onMount stagger={0.07} className="tw:flex tw:flex-col tw:items-center tw:text-center tw:pt-6 tw:pb-10">
            {/* Icon */}
            <motion.div className="tw:mb-5" variants={shouldReduceMotion ? undefined : checkIconPop}>
                <RoundedChecklistIcon color="#3CAEA3" size={46} />
            </motion.div>

            {/* Titles */}
            <StaggerItem className="tw:mb-6">
                <h2 className={TITLE_TEXT}>{t("businessRegister.successPage.title.1")}</h2>
                <h2 className={TITLE_TEXT}>{t("businessRegister.successPage.title.2")}</h2>
            </StaggerItem>

            {/* Descriptions */}
            <StaggerItem className="tw:flex tw:flex-col tw:gap-1 tw:mb-8 tw:max-w-[640px]">
                <HtmlText
                    className={`${BODY_TEXT} tw:!mb-2`}
                    translationKey="businessRegister.successPage.desc.1"
                    values={{ email: profileData.email }}
                />
                <HtmlText className={BODY_TEXT} translationKey="businessRegister.successPage.desc.2" />
                <HtmlText className={BODY_TEXT} translationKey="businessRegister.successPage.desc.3" />
            </StaggerItem>

            {/* Resend email card */}
            <StaggerItem className="tw:w-full tw:md:w-4/5 tw:lg:w-1/2 tw:mb-8">
                <div className="tw:rounded-lg tw:bg-[#F3F3F3] tw:px-6 tw:py-8 tw:sm:px-10">
                    <HtmlText className={`${BODY_TEXT} tw:!mb-6`} translationKey="businessRegister.successPage.card.desc" />
                    <motion.button
                        type="button"
                        className="tw:w-full tw:sm:w-4/5 tw:rounded tw:bg-[#282828] tw:py-3 tw:text-[14px] tw:text-white"
                        onClick={resendEmailHandler}
                        whileHover={pressable.hover}
                        whileTap={pressable.tap}
                    >
                        <span dangerouslySetInnerHTML={{ __html: t("businessRegister.successPage.card.button") }} />
                    </motion.button>
                </div>
            </StaggerItem>

            {/* Footer text */}
            <StaggerItem className="tw:mb-8">
                <HtmlText className={BODY_TEXT} translationKey="businessRegister.successPage.desc.4" />
            </StaggerItem>

            {/* Back to home */}
            <StaggerItem className="tw:w-full tw:flex tw:justify-center">
                <motion.button
                    type="button"
                    className="tw:w-full tw:md:w-1/4 tw:rounded tw:bg-[#DBBE78] tw:py-3 tw:text-[14px] tw:font-bold tw:text-white"
                    onClick={() => history.push("/")}
                    whileHover={pressable.hover}
                    whileTap={pressable.tap}
                >
                    {t("businessRegister.successPage.button")}
                </motion.button>
            </StaggerItem>
        </Stagger>
    );
};

export default SuccessPage;
